import { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CatalogPlacement, Swap, SwapStatus, UserData } from '../ApiTypes';
import { getFaceCroppedSrc } from '../Storage';
import { checkIssue, checkoutSwap, getSwap } from '../api';
import SceneCatalog from '../components/SceneCatalog';
import SwapList from '../components/SwapList';
import SwapPlayer from '../components/SwapPlayer';
import { useUserStateContext } from '../contexts/UserStateContext';
import { CircleLoading, MiniCircleLoading } from '../styles/Loadings';
import { formatDate, useAsyncEffect } from '../utils';

const WatchSwapContainer = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 1440px;
  margin: 0 auto;
  padding: 30px;
  gap: 30px;

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 16px;
    gap: 24px;
  }
`;

const MainContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const SideContainer = styled.div`
  width: 340px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const SwapDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 4px;
  gap: 16px;
`;

const FaceImg = styled.img`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #3a3a3a;
`;

const SwapDescriptionText = styled.div`
  min-width: 0;
`;

const SwapTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #ffffff;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;

  @media (max-width: 560px) {
    font-size: 16px;
  }
`;

const SwapDate = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #8a8a8a;
`;

const InfoContainer = styled.div`
  border-radius: 8px;
  background-color: #262626;
  border-left: 3px solid rgb(254, 167, 0);
  color: #b8b8b8;
  font-size: 13px;
  line-height: 1.6;
  padding: 10px 14px;
  margin-bottom: 16px;
`;

const SwapDescription: FC<{ swap: Swap|null }> = (props) => {
  return <SwapDescriptionContainer>
    {props.swap?.face && <FaceImg src={getFaceCroppedSrc(props.swap.face)} alt="" />}
    <SwapDescriptionText>
      <SwapTitle>{props.swap ? props.swap.original?.detected.title : <Trans>Loading...</Trans>}</SwapTitle>
      {props.swap && <SwapDate>{formatDate(props.swap.createdAt)}</SwapDate>}
    </SwapDescriptionText>
  </SwapDescriptionContainer>
}

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 4px;
`;

const GeneratingNote = styled.div`
  font-size: 14px;
  color: #8a8a8a;
`;

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 18px;
  border-radius: 6px;
  border: 1px solid #4d4d4d;
  color: #f0f0f0;
  font-size: 15px;

  &:hover {
    background-color: #2f2f2f;
  }
`;

const ResultMessage = styled.div`
  padding: 16px 4px;
  font-size: 14px;
  line-height: 1.6;
  color: #b8b8b8;
`;

export const ReportButton = styled.span<{ disabled: boolean }>`
  border-radius: 6px;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  height: 34px;
  padding: 0 18px;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;

  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  background-color: ${props => props.disabled ? '#926613' : 'rgb(254, 167, 0)'};
  color: black;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${props => props.disabled ? '#926613' : '#f8be53'};
  }
`;

const WatchSwapPage: FC<{}> = (props) => {
  const [swap, setSwap] = useState<Swap|null>(null);
  const { swapId } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const generating = searchParams.get("generating") ? true : undefined;
  const userState = useUserStateContext();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserData|null>(null);

  const [haveIssue, setHaveIssue] = useState<boolean|null|undefined>(undefined);

  useAsyncEffect(async () => {
    setUserData(await userState.getUserData());
  }, [userState]);

  useAsyncEffect(async () => {
    if (!swapId || !userState.isSigned) {
      return;
    }
    setHaveIssue(undefined);
    setSwap(null);
    const swap = await getSwap(swapId);
    console.log(swap);
    setSwap(swap);
  }, [swapId, userState]);

  useAsyncEffect(async () => {
    if (!swapId || !swap) {
      return;
    }
    if (swap.status == SwapStatus.PENDING) {
      const result = await checkoutSwap(swapId);
      if (result.actionUrl) {
        window.location.href = result.actionUrl;
      } else {
        const swap = await getSwap(swapId);
        console.log(swap);
        setSwap(swap);
      }
    }
  }, [swap]);

  const handleReportIssue = async () => {
    if (!swapId) {
      return;
    }
    setHaveIssue(null);
    const result = await checkIssue(swapId);
    if (result) {
      navigate("/report-issue");
    } else {
      setHaveIssue(false);
    }
  };

  // 動画を生成中の場合はSwapPlayer内でプログレスバーを表示するので、
  // 動画を生成している場合は、Swapの取得中もSwapPlayerを表示する。
  const isGeneratedAndLoading = !generating && swap && swap.status == SwapStatus.PENDING;

  return <WatchSwapContainer>
    <MainContainer>
      <InfoContainer>
        <Trans>🙏Sorry, due to a system issue, videos may lag in Safari. For better playback, please use Chrome on your PC.</Trans>
      </InfoContainer>
      {isGeneratedAndLoading && <CircleLoading />}
      {!isGeneratedAndLoading && <SwapPlayer swap={swap} generating={generating} />}
      <SwapDescription swap={swap} />
      <ActionContainer>
        {(haveIssue === undefined || haveIssue === null) && <ReportButton disabled={haveIssue === null} onClick={handleReportIssue}>
          {haveIssue === undefined && <Trans>Report an issue</Trans>}
          {haveIssue === null && <MiniCircleLoading />}
          {haveIssue === null && <Trans>Loading...</Trans>}
        </ReportButton>}
        {userData?.downloadable && swap && swap.status != SwapStatus.GENERATED &&
          <GeneratingNote><Trans>Generating</Trans></GeneratingNote>}
        {userData?.downloadable && swap && swap.status == SwapStatus.GENERATED &&
          <DownloadLink href={`https://storages.work/users/${swap.userId}/streamings/${swap.streamingId}/video.mp4`}>
            <Trans>Download</Trans>
          </DownloadLink>}
      </ActionContainer>
      {haveIssue === false && <ResultMessage>
        <Trans>It has been confirmed that there are no problems with video playback. If you are still experiencing issues, please check again in 30 minutes.</Trans>
      </ResultMessage>}
      {/* 見終えた直後が支払い意思のピーク。同じ顔の別シーンをその場に並べる。 */}
      {swap?.faceId && <SceneCatalog
        faceId={swap.faceId}
        placement={CatalogPlacement.WATCH}
        excludeSceneId={swap.sceneId} />}
    </MainContainer>
    <SideContainer>
      <SwapList watchingSwapId={swapId} />
    </SideContainer>
  </WatchSwapContainer>;
};

export default WatchSwapPage;
